import {
  CopyOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Dropdown,
  Input,
  Menu,
  message,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import ImportDataModal from '../../Common/ImportDataModal';
import logo from '../../Images/alux-logo.png';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import formatFile from '../../Images/sample-files/temper-format.csv';
import temperService from '../../services/temper.service';
import { createActivityLog } from '../../slices/activity-log';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';

function TemperList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const { user: currentUser } = useSelector(state => state.auth);
  const ipAddress = useSelector(state => state.ip.ipAddress);
  const [search, setSearch] = useState(
    localStorage.getItem('temper_search') || '',
  );
  const [inputValue, setInputValue] = useState(
    localStorage.getItem('temper_search') || '',
  );

  const fetchTemperList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await temperService.getTemperList(
        page,
        pagesize,
        search || undefined,
        orderBy || undefined,
      );
      if (response.data) {
        const tempers =
          response.data.results?.data ||
          response.data.results ||
          response.data ||
          [];
        setData(Array.isArray(tempers) ? tempers : []);
        setTotalRecord(response.data.count || tempers.length || 0);
      }
    } catch (error) {
      message.error('Failed to fetch tempers');
      console.error('Error fetching tempers:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pagesize, search, orderBy]);

  useEffect(() => {
    fetchTemperList();
  }, [fetchTemperList]);

  useEffect(() => {
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [ipAddress, dispatch]);

  const debouncedAPICall = useMemo(
    () =>
      debounce(value => {
        setSearch(value);
        setPage(1);
        localStorage.setItem('temper_search', value);
      }, 500),
    [],
  );

  const resetFilter = () => {
    setSearch('');
    setInputValue('');
    setOrderBy('');
    setPage(1);
    localStorage.removeItem('temper_search');
    fetchTemperList();
  };

  const handlePageSizeChange = (current, size) => {
    setPage(1);
    setPageSize(size);
  };

  const GetSortingEnumValue = useCallback((sortField, sortOrder) => {
    if (!sortField || !sortOrder) return 'id';
    const prefix = sortOrder === 'descend' ? '-' : '';
    return `${prefix}${sortField}`;
  }, []);

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    if (pagination?.pageSize) setPageSize(pagination.pageSize);
    if (pagination?.current) setPage(pagination.current);
  };

  const handleSearch = e => {
    const value = e.target.value;
    debouncedAPICall(value);
    setInputValue(value);
  };

  const handleEdit = useCallback(record => {
    navigate(`/edit-temper/${record.id}`);
  }, [navigate]);

  const handleView = useCallback(record => {
    navigate(`/view-temper/${record.id}`);
  }, [navigate]);

  const handleCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('Copied to clipboard!');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success('Copied to clipboard!');
      } catch (err) {
        message.error('Failed to copy');
      }
      document.body.removeChild(textArea);
    }
  }, []);

  const handleDelete = async id => {
    try {
      await temperService.deleteTemper(id);
      message.success('Temper archived successfully');
      setSelectedRowKeys([]);
      fetchTemperList();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to archive temper';
      message.error(errorMessage);
    }
  };

  const handleBulkArchive = async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      message.error('Please select at least one record to archive');
      return;
    }

    try {
      await temperService.bulkArchiveTemper(selectedRowKeys);
      message.success(
        `${selectedRowKeys.length} Temper(s) archived successfully`,
      );
      setSelectedRowKeys([]);
      fetchTemperList();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to archive tempers';
      message.error(errorMessage);
    }
  };


  const refreshTable = useCallback(() => {
    fetchTemperList();
  }, [fetchTemperList]);

  const hasPermission = useCallback(
    permission => {
      if (!currentUser?.permissions?.permissions) return false;
      const allPermissions = Object.values(
        currentUser.permissions.permissions,
      ).flat();
      return allPermissions.includes(permission);
    },
    [currentUser],
  );

  const canView = useMemo(() => hasPermission('view_temper'), [hasPermission]);
  const canAdd = useMemo(() => hasPermission('add_temper'), [hasPermission]);
  const canEdit = useMemo(() => hasPermission('change_temper'), [hasPermission]);
  const canDelete = useMemo(
    () => hasPermission('delete_temper'),
    [hasPermission],
  );
  const canDownloadExcel = useMemo(
    () => hasPermission('download_temper_excel_copy'),
    [hasPermission],
  );
  const canDownloadPdf = useMemo(
    () => hasPermission('download_temper_pdf_copy'),
    [hasPermission],
  );

  const dataSource = useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        key: item.id || index,
        SrNo: (page - 1) * pagesize + index + 1,
      })),
    [data, page, pagesize],
  );

  const allColumns = useMemo(
    () => [
      {
        title: 'Sr. No.',
        dataIndex: 'SrNo',
        key: 'SrNo',
        sorter: false,
        width: 80,
      },
      {
        title: 'Temper',
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        width: 150,
      },
      {
        title: 'Temper Code Old',
        dataIndex: 'temper_code_old',
        key: 'temper_code_old',
        sorter: true,
        width: 150,
        render: (text) => <span>{text || '—'}</span>,
      },
      {
        title: 'Temper Code New',
        dataIndex: 'temper_code_new',
        key: 'temper_code_new',
        sorter: true,
        width: 150,
        render: (text) => <span>{text || '—'}</span>,
      },
      {
        title: 'Section Type',
        dataIndex: 'section_type',
        key: 'section_type',
        sorter: true,
        width: 150,
        render: (text, record) => {
          const sectionType = record?.section_type;
          let displayValue = 'N/A';
          if (sectionType && typeof sectionType === 'object') {
            displayValue = sectionType.name || sectionType.section_type_name || 'N/A';
          } else {
            displayValue = record?.section_type_name || 'N/A';
          }
          
          const hasData = displayValue !== 'N/A' && displayValue !== null && displayValue !== undefined;
          
          return (
            <Space size="small">
              {hasData && (
                <Tooltip title="Copy">
                  <CopyOutlined
                    style={{ 
                      cursor: 'pointer', 
                      color: '#1890ff',
                      fontSize: '14px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(displayValue);
                    }}
                  />
                </Tooltip>
              )}
              <span>{displayValue}</span>
            </Space>
          );
        },
      },
      {
        title: 'Area',
        dataIndex: 'area',
        key: 'area',
        sorter: true,
        width: 120,
        render: (text) => <span>{text != null ? text : '—'}</span>,
      },
      {
        title: 'Dimension Unit',
        dataIndex: 'dimention_unit',
        key: 'dimention_unit',
        sorter: true,
        width: 150,
        render: (text, record) => {
          const dimUnit = record?.dimention_unit;
          let displayValue = 'N/A';
          if (dimUnit && typeof dimUnit === 'object') {
            displayValue = dimUnit.uom_name || dimUnit.name || 'N/A';
          } else {
            displayValue = record?.dimention_unit_name || 'N/A';
          }
          
          const hasData = displayValue !== 'N/A' && displayValue !== null && displayValue !== undefined;
          
          return (
            <Space size="small">
              {hasData && (
                <Tooltip title="Copy">
                  <CopyOutlined
                    style={{ 
                      cursor: 'pointer', 
                      color: '#1890ff',
                      fontSize: '14px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(displayValue);
                    }}
                  />
                </Tooltip>
              )}
              <span>{displayValue}</span>
            </Space>
          );
        },
      },
      {
        title: 'Elongation 50mm Min',
        dataIndex: 'elongation_50mm_min',
        key: 'elongation_50mm_min',
        sorter: true,
        width: 150,
        render: (text) => <span>{text != null ? text : '—'}</span>,
      },
      {
        title: 'Elongation Min',
        dataIndex: 'elongation_min',
        key: 'elongation_min',
        sorter: true,
        width: 130,
        render: (text) => <span>{text != null ? text : '—'}</span>,
      },
      {
        title: 'Hardness',
        dataIndex: 'hardness',
        key: 'hardness',
        sorter: true,
        width: 120,
        render: (text) => <span>{text != null ? text : '—'}</span>,
      },
      {
        title: 'Section Thickness Over',
        dataIndex: 'section_thickness_over',
        key: 'section_thickness_over',
        sorter: true,
        width: 180,
        render: (text) => <span>{text || '—'}</span>,
      },
      {
        title: 'Section Thickness Upto',
        dataIndex: 'section_thickness_upto',
        key: 'section_thickness_upto',
        sorter: true,
        width: 180,
        render: (text) => <span>{text || '—'}</span>,
      },
      {
        title: 'Tensile Min',
        dataIndex: 'tensile_min',
        key: 'tensile_min',
        sorter: true,
        width: 120,
        render: (text) => <span>{text != null ? text : '—'}</span>,
      },
      {
        title: 'Tensile Max',
        dataIndex: 'tensile_max',
        key: 'tensile_max',
        sorter: true,
        width: 120,
        render: (text) => <span>{text != null ? text : '—'}</span>,
      },
      {
        title: 'Yield Unit',
        dataIndex: 'yield_unit',
        key: 'yield_unit',
        sorter: true,
        width: 120,
        render: (text, record) => {
          const yieldUnit = record?.yield_unit;
          let displayValue = 'N/A';
          if (yieldUnit && typeof yieldUnit === 'object') {
            displayValue = yieldUnit.name || 'N/A';
          } else {
            displayValue = record?.yield_unit_name || 'N/A';
          }
          
          const hasData = displayValue !== 'N/A' && displayValue !== null && displayValue !== undefined;
          
          return (
            <Space size="small">
              {hasData && (
                <Tooltip title="Copy">
                  <CopyOutlined
                    style={{ 
                      cursor: 'pointer', 
                      color: '#1890ff',
                      fontSize: '14px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(displayValue);
                    }}
                  />
                </Tooltip>
              )}
              <span>{displayValue}</span>
            </Space>
          );
        },
      },
      {
        title: 'Yield Min',
        dataIndex: 'yield_min',
        key: 'yield_min',
        sorter: true,
        width: 120,
        render: (text) => <span>{text != null ? text : '—'}</span>,
      },
      {
        title: 'Yield Max',
        dataIndex: 'yield_max',
        key: 'yield_max',
        sorter: true,
        width: 120,
        render: (text) => <span>{text != null ? text : '—'}</span>,
      },
      {
        title: 'Electrical Conductivity Min',
        dataIndex: 'electrical_conductivity_min',
        key: 'electrical_conductivity_min',
        sorter: true,
        width: 200,
        render: (text) => <span>{text != null ? text : '—'}</span>,
      },
      {
        title: 'Electrical Conductivity Max',
        dataIndex: 'electrical_conductivity_max',
        key: 'electrical_conductivity_max',
        sorter: true,
        width: 200,
        render: (text) => <span>{text != null ? text : '—'}</span>,
      },
      {
        title: 'Created At',
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 150,
        render: (text, record) => {
          return (
            <span>
              {record?.created_at
                ? dayjs(record.created_at).format('DD-MM-YYYY HH:mm')
                : 'N/A'}
            </span>
          );
        },
      },
      {
        title: 'Created By',
        dataIndex: 'created_by',
        key: 'created_by',
        sorter: true,
        width: 150,
        render: (text, record) => {
          const createdBy = record?.created_by;
          if (createdBy && typeof createdBy === 'object') {
            return (
              <span>
                {createdBy.first_name || ''} {createdBy.last_name || ''}
              </span>
            );
          }
          return <span>N/A</span>;
        },
      },
      {
        title: 'Updated At',
        dataIndex: 'updated_at',
        key: 'updated_at',
        sorter: true,
        width: 150,
        render: (text, record) => {
          return (
            <span>
              {record?.updated_at
                ? dayjs(record.updated_at).format('DD-MM-YYYY HH:mm')
                : 'N/A'}
            </span>
          );
        },
      },
      {
        title: 'Updated By',
        dataIndex: 'updated_by',
        key: 'updated_by',
        sorter: true,
        width: 150,
        render: (text, record) => {
          const updatedBy = record?.updated_by;
          if (updatedBy && typeof updatedBy === 'object') {
            return (
              <span>
                {updatedBy.first_name || ''} {updatedBy.last_name || ''}
              </span>
            );
          }
          return <span>N/A</span>;
        },
      },
      {
        title: 'Action',
        dataIndex: '',
        key: 'action',
        fixed: 'right',
        width: 200,
        render: (text, record) => (
          <Space>
            {canView && (
              <Tooltip title="View">
                <span
                  onClick={() => handleView(record)}
                  style={{ cursor: 'pointer' }}
                >
                  <EyeOutlined style={{ fontSize: '17px' }} />
                </span>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="Edit">
                <span
                  onClick={() => handleEdit(record)}
                  style={{ cursor: 'pointer' }}
                >
                  <EditIcon />
                </span>
              </Tooltip>
            )}
            {canDelete && (
              <Popconfirm
                title="Are you sure you want to archive this temper?"
                onConfirm={() => handleDelete(record.id)}
                onCancel={() => message.info('Action cancelled')}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip placement="top" title={'Archive'}>
                  <span style={{ cursor: 'pointer', display: 'inline-block' }}>
                    <Archive />
                  </span>
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [handleEdit, handleView, handleDelete, handleCopy, canView, canEdit, canDelete],
  );

  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'name',
    'temper_code_old',
    'temper_code_new',
    'section_type',
    'created_at',
    'created_by',
    'updated_at',
    'updated_by',
    'action',
  ]);

  const csvheaders = [
    { label: 'Sr. No.', key: 'SrNo' },
    { label: 'Temper', key: 'name' },
    { label: 'Temper Code Old', key: 'temper_code_old' },
    { label: 'Temper Code New', key: 'temper_code_new' },
    { label: 'Section Type', key: 'section_type' },
    { label: 'Area', key: 'area' },
    { label: 'Dimension Unit', key: 'dimention_unit' },
    { label: 'Elongation 50mm Min', key: 'elongation_50mm_min' },
    { label: 'Elongation Min', key: 'elongation_min' },
    { label: 'Hardness', key: 'hardness' },
    { label: 'Section Thickness Over', key: 'section_thickness_over' },
    { label: 'Section Thickness Upto', key: 'section_thickness_upto' },
    { label: 'Tensile Min', key: 'tensile_min' },
    { label: 'Tensile Max', key: 'tensile_max' },
    { label: 'Yield Unit', key: 'yield_unit' },
    { label: 'Yield Min', key: 'yield_min' },
    { label: 'Yield Max', key: 'yield_max' },
    { label: 'Electrical Conductivity Min', key: 'electrical_conductivity_min' },
    { label: 'Electrical Conductivity Max', key: 'electrical_conductivity_max' },
    { label: 'Created At', key: 'created_at' },
    { label: 'Created By', key: 'created_by' },
    { label: 'Updated At', key: 'updated_at' },
    { label: 'Updated By', key: 'updated_by' },
  ];

  const csvFilterheaders = csvheaders.filter(header =>
    checkedList.includes(header.key),
  );

  const csvData = useMemo(
    () =>
      dataSource.map(item => {
        const sectionType = item?.section_type;
        const sectionTypeName = sectionType && typeof sectionType === 'object'
          ? (sectionType.name || sectionType.section_type_name || 'N/A')
          : (item?.section_type_name || 'N/A');

        const dimUnit = item?.dimention_unit;
        const dimUnitName = dimUnit && typeof dimUnit === 'object'
          ? (dimUnit.uom_name || dimUnit.name || 'N/A')
          : (item?.dimention_unit_name || 'N/A');

        const yieldUnit = item?.yield_unit;
        const yieldUnitName = yieldUnit && typeof yieldUnit === 'object'
          ? (yieldUnit.name || 'N/A')
          : (item?.yield_unit_name || 'N/A');

        return {
          SrNo: item.SrNo,
          name: item.name || 'N/A',
          temper_code_old: item.temper_code_old || '',
          temper_code_new: item.temper_code_new || '',
          section_type: sectionTypeName,
          area: item.area != null ? item.area : '',
          dimention_unit: dimUnitName,
          elongation_50mm_min: item.elongation_50mm_min != null ? item.elongation_50mm_min : '',
          elongation_min: item.elongation_min != null ? item.elongation_min : '',
          hardness: item.hardness != null ? item.hardness : '',
          section_thickness_over: item.section_thickness_over || '',
          section_thickness_upto: item.section_thickness_upto || '',
          tensile_min: item.tensile_min != null ? item.tensile_min : '',
          tensile_max: item.tensile_max != null ? item.tensile_max : '',
          yield_unit: yieldUnitName,
          yield_min: item.yield_min != null ? item.yield_min : '',
          yield_max: item.yield_max != null ? item.yield_max : '',
          electrical_conductivity_min: item.electrical_conductivity_min != null ? item.electrical_conductivity_min : '',
          electrical_conductivity_max: item.electrical_conductivity_max != null ? item.electrical_conductivity_max : '',
          created_at: item.created_at
            ? dayjs(item.created_at).format('DD-MM-YYYY HH:mm')
            : 'N/A',
          created_by: (() => {
            const createdBy = item?.created_by;
            if (createdBy && typeof createdBy === 'object') {
              const firstName = createdBy.first_name?.replace(/[()']/g, '').trim() || '';
              const lastName = createdBy.last_name?.trim() || '';
              return `${firstName} ${lastName}`.trim() || 'N/A';
            }
            if (item?.created_by_info) {
              const firstName = item.created_by_info.first_name?.replace(/[()']/g, '').trim() || '';
              const lastName = item.created_by_info.last_name?.trim() || '';
              return `${firstName} ${lastName}`.trim() || 'N/A';
            }
            return 'N/A';
          })(),
          updated_at: item.updated_at
            ? dayjs(item.updated_at).format('DD-MM-YYYY HH:mm')
            : 'N/A',
          updated_by: (() => {
            const updatedBy = item?.updated_by;
            if (updatedBy && typeof updatedBy === 'object') {
              const firstName = updatedBy.first_name?.replace(/[()']/g, '').trim() || '';
              const lastName = updatedBy.last_name?.trim() || '';
              return `${firstName} ${lastName}`.trim() || 'N/A';
            }
            if (item?.updated_by_info) {
              const firstName = item.updated_by_info.first_name?.replace(/[()']/g, '').trim() || '';
              const lastName = item.updated_by_info.last_name?.trim() || '';
              return `${firstName} ${lastName}`.trim() || 'N/A';
            }
            return 'N/A';
          })(),
        };
      }),
    [dataSource],
  );

  const handlePdfDownload = async () => {
    const pdfColumns = [
      { header: 'Sr. No.', dataKey: 'SrNo' },
      { header: 'Temper', dataKey: 'name' },
      { header: 'Code Old', dataKey: 'temper_code_old' },
      { header: 'Code New', dataKey: 'temper_code_new' },
      { header: 'Section Type', dataKey: 'section_type' },
      { header: 'Area', dataKey: 'area' },
      { header: 'Dim Unit', dataKey: 'dimention_unit' },
      { header: 'Elong 50mm Min', dataKey: 'elongation_50mm_min' },
      { header: 'Elong Min', dataKey: 'elongation_min' },
      { header: 'Hardness', dataKey: 'hardness' },
      { header: 'Sec Thick Over', dataKey: 'section_thickness_over' },
      { header: 'Sec Thick Upto', dataKey: 'section_thickness_upto' },
      { header: 'Tensile Min', dataKey: 'tensile_min' },
      { header: 'Tensile Max', dataKey: 'tensile_max' },
      { header: 'Yield Unit', dataKey: 'yield_unit' },
      { header: 'Yield Min', dataKey: 'yield_min' },
      { header: 'Yield Max', dataKey: 'yield_max' },
      { header: 'Elec Cond Min', dataKey: 'electrical_conductivity_min' },
      { header: 'Elec Cond Max', dataKey: 'electrical_conductivity_max' },
      { header: 'Created At', dataKey: 'created_at' },
      { header: 'Created By', dataKey: 'created_by' },
      { header: 'Updated At', dataKey: 'updated_at' },
      { header: 'Updated By', dataKey: 'updated_by' },
    ];

    const pdffilteredColumns = pdfColumns.filter(column =>
      checkedList.includes(column.dataKey),
    );

    // Define optimal column widths based on content type
    const columnWidths = {
      'SrNo': 40,
      'name': 80,
      'temper_code_old': 70,
      'temper_code_new': 70,
      'section_type': 90,
      'area': 60,
      'dimention_unit': 80,
      'elongation_50mm_min': 80,
      'elongation_min': 70,
      'hardness': 60,
      'section_thickness_over': 90,
      'section_thickness_upto': 90,
      'tensile_min': 70,
      'tensile_max': 70,
      'yield_unit': 70,
      'yield_min': 70,
      'yield_max': 70,
      'electrical_conductivity_min': 100,
      'electrical_conductivity_max': 100,
      'created_at': 100,
      'created_by': 100,
      'updated_at': 100,
      'updated_by': 100,
    };

    // Calculate total width needed
    const totalWidthNeeded = pdffilteredColumns.reduce((sum, col) => {
      return sum + (columnWidths[col.dataKey] || 70);
    }, 0);

    // A4 dimensions: Portrait (595pt width) vs Landscape (842pt width)
    // Use landscape if total width needed exceeds portrait width threshold or too many columns
    // Threshold: 480pt (leaving margin for padding ~80pt on each side)
    const portraitWidth = 595;
    const landscapeWidth = 842;
    const portraitThreshold = 480; // Available width in portrait after margins
    const columnCountThreshold = 10; // Use landscape if more than 10 columns
    
    // Determine orientation: use landscape if width exceeds threshold OR too many columns
    const useLandscape = totalWidthNeeded > portraitThreshold || pdffilteredColumns.length > columnCountThreshold;

    // Create PDF with appropriate orientation
    const pdf = new jsPDF(useLandscape ? 'l' : 'p', 'pt', 'a4');

    const rows = dataSource.map((item) => {
      const sectionType = item?.section_type;
      const sectionTypeName = sectionType && typeof sectionType === 'object'
        ? (sectionType.name || sectionType.section_type_name || 'N/A')
        : (item?.section_type_name || 'N/A');

      const dimUnit = item?.dimention_unit;
      const dimUnitName = dimUnit && typeof dimUnit === 'object'
        ? (dimUnit.uom_name || dimUnit.name || 'N/A')
        : (item?.dimention_unit_name || 'N/A');

      const yieldUnit = item?.yield_unit;
      const yieldUnitName = yieldUnit && typeof yieldUnit === 'object'
        ? (yieldUnit.name || 'N/A')
        : (item?.yield_unit_name || 'N/A');

      return {
        SrNo: item.SrNo,
        name: item.name || 'N/A',
        temper_code_old: item.temper_code_old || '—',
        temper_code_new: item.temper_code_new || '—',
        section_type: sectionTypeName,
        area: item.area != null ? String(item.area) : '—',
        dimention_unit: dimUnitName,
        elongation_50mm_min: item.elongation_50mm_min != null ? String(item.elongation_50mm_min) : '—',
        elongation_min: item.elongation_min != null ? String(item.elongation_min) : '—',
        hardness: item.hardness != null ? String(item.hardness) : '—',
        section_thickness_over: item.section_thickness_over || '—',
        section_thickness_upto: item.section_thickness_upto || '—',
        tensile_min: item.tensile_min != null ? String(item.tensile_min) : '—',
        tensile_max: item.tensile_max != null ? String(item.tensile_max) : '—',
        yield_unit: yieldUnitName,
        yield_min: item.yield_min != null ? String(item.yield_min) : '—',
        yield_max: item.yield_max != null ? String(item.yield_max) : '—',
        electrical_conductivity_min: item.electrical_conductivity_min != null ? String(item.electrical_conductivity_min) : '—',
        electrical_conductivity_max: item.electrical_conductivity_max != null ? String(item.electrical_conductivity_max) : '—',
        created_at: item.created_at
          ? dayjs(item.created_at).format('DD-MM-YYYY HH:mm')
          : 'N/A',
        created_by: (() => {
          const createdBy = item?.created_by;
          if (createdBy && typeof createdBy === 'object') {
            const firstName = createdBy.first_name?.replace(/[()']/g, '').trim() || '';
            const lastName = createdBy.last_name?.trim() || '';
            return `${firstName} ${lastName}`.trim() || 'N/A';
          }
          if (item?.created_by_info) {
            const firstName = item.created_by_info.first_name?.replace(/[()']/g, '').trim() || '';
            const lastName = item.created_by_info.last_name?.trim() || '';
            return `${firstName} ${lastName}`.trim() || 'N/A';
          }
          return 'N/A';
        })(),
        updated_at: item.updated_at
          ? dayjs(item.updated_at).format('DD-MM-YYYY HH:mm')
          : 'N/A',
        updated_by: (() => {
          const updatedBy = item?.updated_by;
          if (updatedBy && typeof updatedBy === 'object') {
            const firstName = updatedBy.first_name?.replace(/[()']/g, '').trim() || '';
            const lastName = updatedBy.last_name?.trim() || '';
            return `${firstName} ${lastName}`.trim() || 'N/A';
          }
          if (item?.updated_by_info) {
            const firstName = item.updated_by_info.first_name?.replace(/[()']/g, '').trim() || '';
            const lastName = item.updated_by_info.last_name?.trim() || '';
            return `${firstName} ${lastName}`.trim() || 'N/A';
          }
          return 'N/A';
        })(),
      };
    });

    const pageWidth = pdf.internal.pageSize.width;
    const pageHeight = pdf.internal.pageSize.height;

    // Logo and Heading
    pdf.addImage(logo, 'PNG', 40, 10, 90, 30);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(16);
    const headingText = 'Temper List';
    const headingTextWidth = pdf.getTextWidth(headingText);
    const headingX = (pageWidth - headingTextWidth) / 2;
    pdf.text(headingText, headingX, 30);

    // Calculate available width and scale factor
    const availableWidth = pageWidth - 80;
    let scaleFactor = 1;
    
    // For portrait mode with fewer columns, expand to use full width
    if (!useLandscape && totalWidthNeeded < availableWidth) {
      // Expand columns to fill available space in portrait mode
      scaleFactor = availableWidth / totalWidthNeeded;
    } else if (totalWidthNeeded > availableWidth) {
      // Scale down if columns exceed available width
      scaleFactor = availableWidth / totalWidthNeeded;
    }

    // Adjust font size based on orientation
    const baseFontSize = useLandscape ? 6 : 7;
    const cellPadding = useLandscape ? 1.5 : 2;

    pdf.autoTable({
      columns: pdffilteredColumns,
      body: rows,
      startY: 50,
      theme: 'grid',
      styles: {
        font: 'times',
        fontSize: baseFontSize,
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: cellPadding,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
        valign: 'middle',
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: baseFontSize,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
        halign: 'center',
        valign: 'middle',
      },
      columnStyles: pdffilteredColumns.reduce((acc, col) => {
        const baseWidth = columnWidths[col.dataKey] || 70;
        acc[col.dataKey] = { 
          cellWidth: baseWidth * scaleFactor,
          overflow: 'linebreak',
          cellPadding: cellPadding,
        };
        return acc;
      }, {}),
      rowStyles: {
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fontSize: 6,
      },
      tableLineColor: [0, 0, 0],
      rowPageBreak: 'avoid',
      showHead: 'everyPage',
      margin: { top: 50, left: 40, right: 40, bottom: 30 },
      didDrawPage: data => {
        const pageNumber = pdf.internal.getCurrentPageInfo().pageNumber;
        const totalPages = pdf.internal.getNumberOfPages();

        // Footer (Date & Time left, Page Number right)
        const currentDate = moment().format('DD-MM-YYYY HH:mm');
        pdf.setFontSize(10);
        pdf.text(currentDate, 40, pageHeight - 20);
        pdf.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - 100, pageHeight - 20);
      },
    });

    pdf.save(`temper_list_${moment().format('YYYY-MM-DD')}.pdf`);

    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Temper',
        ip_address: ipAddress,
        discription: 'Temper PDF download successfully!',
      }),
    );
  };

  const activityLog = async () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Temper',
        ip_address: ipAddress,
        discription: 'Temper Excel download successfully!',
      }),
    );
  };

  const handleColumnVisibilityChange = useCallback(checkedValues => {
    setCheckedList(checkedValues);
  }, []);

  const filteredColumns = useMemo(
    () => allColumns.filter(column => checkedList.includes(column.key)),
    [allColumns, checkedList],
  );

  const columnMenu = useMemo(
    () => (
      <Menu className="packagelistdropdown">
        <Menu.Item key="columnMenu">
          <Checkbox.Group
            value={checkedList}
            onChange={handleColumnVisibilityChange}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Checkbox value="SrNo" disabled>
                  Sr. No.
                </Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="name">Temper</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="temper_code_old">Temper Code Old</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="temper_code_new">Temper Code New</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="section_type">Section Type</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="area">Area</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="dimention_unit">Dimension Unit</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="elongation_50mm_min">Elongation 50mm Min</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="elongation_min">Elongation Min</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="hardness">Hardness</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="section_thickness_over">Section Thickness Over</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="section_thickness_upto">Section Thickness Upto</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="tensile_min">Tensile Min</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="tensile_max">Tensile Max</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="yield_unit">Yield Unit</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="yield_min">Yield Min</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="yield_max">Yield Max</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="electrical_conductivity_min">Electrical Conductivity Min</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="electrical_conductivity_max">Electrical Conductivity Max</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="created_at">Created At</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="created_by">Created By</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="updated_at">Updated At</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="updated_by">Updated By</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="action" disabled>
                  Action
                </Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Menu.Item>
      </Menu>
    ),
    [checkedList, handleColumnVisibilityChange],
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  if (!canView) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <>
      <div className="sticyaction">
        <Row className="pagenamerow mb-0" justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Temper Master</h2>
            <div className="bredcrumbwrp">
              <Link to="/dashboard" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Temper Master' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space>
              {canAdd && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/add-temper')}
                >
                  Add Temper
                </Button>
              )}
              {canAdd && (
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setImportModalOpen(true)}
                >
                  Bulk Import
                </Button>
              )}
              {selectedRowKeys.length > 0 && canDelete && (
                <Popconfirm
                  title={`Are you sure you want to archive ${selectedRowKeys.length} selected temper(s)?`}
                  onConfirm={handleBulkArchive}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="primary" danger>
                    Bulk Archive ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              )}
              {canDownloadExcel && (
                <Tooltip placement="top" title={'Export Excel'}>
                  <Button
                    type="primary"
                    className="iconlink"
                    style={{ width: '10px' }}
                    onClick={activityLog}
                  >
                    <CSVLink
                      filename={`temper_${new Date().toISOString().split('T')[0]}.csv`}
                      data={csvData}
                      className="iconlink"
                      headers={csvFilterheaders}
                    >
                      <FileExcelOutlined />
                    </CSVLink>
                  </Button>
                </Tooltip>
              )}
              {canDownloadPdf && (
                <Tooltip placement="top" title={'Export PDF'}>
                  <Button
                    type="primary"
                    className="iconlink"
                    onClick={handlePdfDownload}
                  >
                    <FilePdfOutlined />
                  </Button>
                </Tooltip>
              )}
              <Link to="/archive-temper-list" className="iconlink">
                <Tooltip title="Archive">
                  <Archiveicon />
                </Tooltip>
              </Link>
              <div className="gridiconswrap">
                <Tooltip placement="topLeft" title="Column Filter Options">
                  <Dropdown
                    overlay={columnMenu}
                    trigger={['click']}
                    placement="bottomRight"
                    visible={dropdownVisible}
                    onVisibleChange={setDropdownVisible}
                  >
                    <Button
                      type="primary"
                      icon={<ColumnList />}
                      onClick={e => e.preventDefault()}
                    />
                  </Dropdown>
                </Tooltip>
              </div>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="formwrapper">
        <Row gutter={16} className="mb-3">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              placeholder="Search by name or code"
              prefix={<SearchOutlined />}
              value={inputValue}
              onChange={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Button onClick={resetFilter}>Reset</Button>
          </Col>
        </Row>
      </div>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div className="tablewrp">
            <Table
              rowKey="id"
              columns={filteredColumns}
              dataSource={dataSource}
              pagination={false}
              loading={loading}
              onChange={handleTableChange}
              rowSelection={canDelete ? rowSelection : null}
              scroll={{ x: 'max-content' }}
              locale={{
                emptyText: 'No tempers found',
              }}
            />
          </div>
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            pageSizeOptions={['20', '50', '100']}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>

      <ImportDataModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        refreshTable={refreshTable}
        formatFilePath={formatFile}
        model="temper"
      />
    </>
  );
}

export default TemperList;
